import React, { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  REPORTS, activityFamily, ACTIVITIES, SESSIONS,
  Org, RiskLevel,
} from "../mockData";
import { StatCard } from "../components/StatCard";
import { ActivityFilter, FilterCard, OptionFilter } from "../components/VisualFilter";
import { FileText, FileCode, Download, Share2, Search } from "lucide-react";
import { useApiResource } from "../api/useApi";
import { listUploads } from "../api/resources";

export default function Reports() {
  const { org } = useOutletContext<{ org: Org }>();

  const [sport, setSport] = useState<string>("all");
  const [format, setFormat] = useState<"all" | "PDF" | "HTML">("all");
  const [q, setQ] = useState("");

  // Pulls live uploads so the count of available reports tracks the API.
  // Per-report bodies (videoReportDetailApp) are fetched lazily by the row
  // when the backend DTO is wired into the row component.
  useApiResource((apiFetch) => listUploads(apiFetch), []);

  const filtered = useMemo(() => {
    return REPORTS.filter((r) => {
      const isWp = r.activity.toLowerCase().startsWith("workplace");
      const activityId = ACTIVITIES.find((s) => r.activity.includes(s.label.split(" — ")[0]))?.id;
      const fam = isWp ? "Workplace" : activityId ? activityFamily(activityId) : "Movement";

      if (sport !== "all" && fam !== sport) return false;
      if (format !== "all" && r.format !== format) return false;
      if (q && !r.athlete.toLowerCase().includes(q.toLowerCase()) &&
              !r.highlights.join(" ").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [sport, format, q]);

  // Risk distribution sourced from any sessions in this org that carry a
  // risk tag — not gated by org type, since any org may run task assessments.
  const dist = useMemo(() => {
    const r = SESSIONS.filter((s) => s.orgId === org.id && s.risk);
    if (r.length === 0) return null;
    const out: Record<RiskLevel, number> = { green: 0, yellow: 0, red: 0 };
    for (const s of r) if (s.risk) out[s.risk] += 1;
    return out;
  }, [org.id]);

  const activeCount =
    (sport !== "all" ? 1 : 0) + (format !== "all" ? 1 : 0) + (q ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* Filter row — universal top placement */}
      <FilterCard
        countActive={activeCount}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search reports, athletes, highlights…"
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
          </div>
        }
        summary={[
          { label: "Activity",  value: sport === "all" ? "All" : sport,
            onClear: sport !== "all" ? () => setSport("all") : undefined },
          { label: "Format", value: format === "all" ? "All" : format,
            onClear: format !== "all" ? () => setFormat("all") : undefined },
        ]}
      >
        <ActivityFilter value={sport} onChange={setSport} />
        <OptionFilter
          label="Format"
          value={format}
          onChange={(v) => setFormat(v as any)}
          options={[
            { value: "PDF",  label: "PDF",  emoji: "📄" },
            { value: "HTML", label: "HTML", emoji: "🌐" },
          ]}
        />
      </FilterCard>

      {/* KPI strip — uniform across orgs. If the org has any risk-tagged
          sessions, show risk distribution; otherwise show report mix. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {dist ? (
          <>
            <StatCard label="High-risk tasks"     value={dist.red}    unit="red"     delta={-15} trend={[6,5,5,4,3,3,3]} intent="negative" />
            <StatCard label="Moderate-risk tasks" value={dist.yellow} unit="yellow"  delta={-4}  trend={[8,7,7,6,6,5,5]} intent="negative" />
            <StatCard label="Low-risk tasks"      value={dist.green}  unit="green"   delta={12}  trend={[3,4,5,5,6,6,7]} intent="positive" />
            <StatCard label="Reports this week"   value={42}          delta={11.2}   trend={[28,30,33,36,38,40,42]} intent="positive" />
          </>
        ) : (
          <>
            <StatCard label="Reports this week"   value={42}  delta={11.2}  trend={[28, 30, 33, 36, 38, 40, 42]}    intent="positive" />
            <StatCard label="Avg generation time" value={6.4} unit="sec"   delta={-12.5} trend={[10, 9, 8.5, 8, 7.2, 6.8, 6.4]} intent="negative" />
            <StatCard label="PDF share"           value="58%" delta={2.1}  trend={[50, 52, 54, 55, 56, 57, 58]} />
            <StatCard label="HTML share"          value="42%" delta={-2.1} trend={[50, 48, 46, 45, 44, 43, 42]} />
          </>
        )}
      </div>

      {/* Result count */}
      <div className="text-xs text-slate-500 px-1">
        {filtered.length} of {REPORTS.length} reports
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((r) => (
          <div key={r.id} className="card p-5 hover:ring-1 hover:ring-el-200 transition">
            <div className="flex items-start gap-3">
              <div className={`h-10 w-10 rounded-md flex items-center justify-center ${
                r.format === "PDF"
                  ? "bg-rose-50 text-rose-600 ring-1 ring-rose-100"
                  : "bg-violet-50 text-violet-600 ring-1 ring-violet-100"
              }`}>
                {r.format === "PDF" ? <FileText className="h-5 w-5" /> : <FileCode className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900">{r.athlete}</div>
                  <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                    {r.format}
                  </span>
                </div>
                <div className="text-xs text-slate-500">
                  {r.activity} · {r.generatedAt}
                </div>
              </div>
            </div>

            <ul className="mt-4 space-y-1.5">
              {r.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-xs text-slate-700">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-el-500 shrink-0" />
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3">
              <button className="btn-secondary !h-8 !text-xs"><Download className="h-3.5 w-3.5" /> Download</button>
              <button className="btn-secondary !h-8 !text-xs"><Share2 className="h-3.5 w-3.5" /> Share link</button>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="card p-12 text-center text-sm text-slate-500">
          No reports match your filters.
        </div>
      )}
    </div>
  );
}
